import type { ComponentType } from 'react'
import type { Subcategory } from '~/entities/categories'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { getIsFeatureEnabled } from '~/shared/lib/feature-flags'
import type {
  AuditTrailEventProps,
  AuditTrailStep,
  ClassificationEventProps,
  ClusterEventProps,
  DataExtractionEventProps,
  EobDuplicationEventProps,
  EobPaymentDepositEventProps,
  EobProcessingEventProps,
  EobProcessingFinishedEventProps,
  OnshoreChangeEventProps,
  PatientMatchEventProps,
  PayorChangeEventProps,
  PayorEventProps,
  TaggingEventProps,
} from '../model'
import {
  ClassificationEvent,
  ClusterEvent,
  DataExtractionEvent,
  EobDuplicationEvent,
  EobPaymentDepositEvent,
  EobProcessingEvent,
  EobProcessingFinishedEvent,
  OnshoreChangeEvent,
  PatientMatchEvent,
  PayorChangeEvent,
  PayorEvent,
  TaggingEvent,
} from '../ui/events'
import { computeEventMeta } from './pre-pass'
import {
  getClassificationSearchableText,
  getClusterSearchableText,
  getDataExtractionSearchableText,
  getEobDuplicationSearchableText,
  getEobPaymentDepositSearchableText,
  getEobProcessingFinishedSearchableText,
  getEobProcessingSearchableText,
  getOnshoreChangeSearchableText,
  getPatientMatchSearchableText,
  getPayorChangeSearchableText,
  getPayorSearchableText,
  getTaggingSearchableText,
  transformClassification,
  transformCluster,
  transformDataExtraction,
  transformEobDuplication,
  transformEobPaymentDeposit,
  transformEobProcessing,
  transformEobProcessingFinished,
  transformOnshoreChange,
  transformPatientMatch,
  transformPayor,
  transformPayorChange,
  transformTagging,
} from './transforms'

// Per-type component props (each component receives its own typed props + searchQuery)
export type ClusterEventComponentProps = { event: ClusterEventProps; searchQuery: string }
export type ClassificationEventComponentProps = {
  event: ClassificationEventProps
  searchQuery: string
}
export type DataExtractionEventComponentProps = {
  event: DataExtractionEventProps
  searchQuery: string
}
export type TaggingEventComponentProps = { event: TaggingEventProps; searchQuery: string }
export type PatientMatchEventComponentProps = {
  event: PatientMatchEventProps
  searchQuery: string
}
export type PayorEventComponentProps = { event: PayorEventProps; searchQuery: string }
export type PayorChangeEventComponentProps = {
  event: PayorChangeEventProps
  searchQuery: string
}
export type EobPaymentDepositEventComponentProps = {
  event: EobPaymentDepositEventProps
  searchQuery: string
}
export type EobDuplicationEventComponentProps = {
  event: EobDuplicationEventProps
  searchQuery: string
}
export type EobProcessingEventComponentProps = {
  event: EobProcessingEventProps
  searchQuery: string
}
export type EobProcessingFinishedEventComponentProps = {
  event: EobProcessingFinishedEventProps
  searchQuery: string
}
export type OnshoreChangeEventComponentProps = {
  event: OnshoreChangeEventProps
  searchQuery: string
}

/** Context needed by per-type transforms */
export interface TransformContext {
  subcategories: Subcategory[]
  payors: apiClient.Payor[]
  isTaggingEnabled: boolean
  hideLlmRarcCarcUntilReviewed?: boolean
  isTaggingFinished?: boolean
  hideLlmClassificationUntilReviewed?: boolean
  isClassificationFinished?: boolean
  paPatientData?: apiClient.PatientPaRecord[]
}

function getStepByEvent(event: apiClient.AuditEvent, isEob: boolean): AuditTrailStep | null {
  switch (event.event_type as string) {
    case 'cluster':
      return 'clustering'
    case 'class':
    case 'mbclass':
    case 'clsrev':
      return 'classification'
    case 'datallm':
    case 'datarev':
      if (event.data_content?.type === 'denial') {
        return 'data'
      }
      return 'tagging'
    case 'payors':
    case 'payorchange':
      return isEob ? 'eob-payment-deposit' : 'data'
    case 'tagsllm':
    case 'tagsrev':
      return 'tagging'
    case 'patientrev':
    case 'pa-patientrev':
    case 'patientlookup':
    case 'pa-patientlookup':
      if (
        event.event_type === 'pa-patientlookup' &&
        !getIsFeatureEnabled('2026-09-auto-reviewed-pa-selection')
      ) {
        return null
      }
      return 'patient'
    case 'eob_payment_deposit':
      return 'eob-payment-deposit'
    case 'eob_duplication':
      return 'eob-duplication'
    case 'eob_payment_summary_updated':
    case 'eob_patient_claims_updated':
    case 'eob_processing_finished':
      return 'eob-processing'
    case 'onshorechange':
      return 'document'
    default:
      return null
  }
}

/** Replaces the old transformAuditTrailEvents — filters, pre-passes, and per-type transforms */
export function processEvents(
  events: apiClient.AuditEvent[],
  context: TransformContext,
): AuditTrailEventProps[] {
  const {
    subcategories,
    payors,
    isTaggingEnabled,
    hideLlmRarcCarcUntilReviewed,
    isTaggingFinished,
    hideLlmClassificationUntilReviewed,
    isClassificationFinished,
  } = context
  const usesNewTagsApi = events.some((e) => ['tagsllm', 'tagsrev'].includes(e.event_type))
  const isEob = events.some((e) => e.event_type.startsWith('eob_'))

  // Pre-filter: remove comment-only events and carc-rarc events when tagging enabled + new tags API
  const filteredEvents = events
    .filter(
      (event) =>
        !(event.event_type === 'clsrev' && !event.category && !!event.notes) &&
        !(event.event_type === 'patientrev' && !event.status && !!event.notes) &&
        !(event.event_type === 'pa-patientrev' && !event.status && !!event.notes),
    )
    .filter((event) => {
      if (
        hideLlmClassificationUntilReviewed &&
        !isClassificationFinished &&
        (event.event_type === 'class' || event.event_type === 'mbclass')
      ) {
        return false
      }
      if (!isTaggingEnabled) {
        return true
      }
      if (
        usesNewTagsApi &&
        ['datallm', 'datarev'].includes(event.event_type) &&
        event.data_content?.type === 'carc-rarc'
      ) {
        return false
      }
      if (
        hideLlmRarcCarcUntilReviewed &&
        !isTaggingFinished &&
        ['tagsllm', 'datallm'].includes(event.event_type)
      ) {
        return false
      }
      return true
    })

  // Pre-pass: compute revalidated flags
  const metaMap = computeEventMeta(filteredEvents)

  // Per-type transforms
  const result: AuditTrailEventProps[] = []
  for (let index = 0; index < filteredEvents.length; index++) {
    const event = filteredEvents[index]
    const step = getStepByEvent(event, isEob)
    if (!step) continue

    // Merge 'denial' and 'carc-rarc' events into a single event — skip carc-rarc when legacy
    if (event.data_content?.type === 'carc-rarc' && !isTaggingEnabled) {
      continue
    }

    const meta = metaMap.get(index)!
    let props: AuditTrailEventProps | null = null

    switch (step) {
      case 'clustering':
        props = transformCluster(event)
        break
      case 'classification':
        props = transformClassification(event, meta, subcategories)
        break
      case 'data':
        if (event.event_type === 'payors') {
          props = transformPayor(event)
        } else if (event.event_type === 'payorchange') {
          props = transformPayorChange(event, payors)
        } else {
          props = transformDataExtraction(event, filteredEvents, index, meta, isTaggingEnabled)
        }
        break
      case 'tagging':
        props = transformTagging(event, filteredEvents, meta)
        break
      case 'patient':
        props = transformPatientMatch(event, filteredEvents, meta, context.paPatientData)
        break
      case 'eob-payment-deposit':
        if (event.event_type === 'payors') {
          props = transformPayor(event, true)
        } else if (event.event_type === 'payorchange') {
          props = transformPayorChange(event, payors, true)
        } else {
          props = transformEobPaymentDeposit(event, filteredEvents, index)
        }
        break
      case 'eob-duplication':
        props = transformEobDuplication(event, filteredEvents, index)
        break
      case 'eob-processing':
        if (event.event_type === 'eob_processing_finished') {
          props = transformEobProcessingFinished(event, filteredEvents, index)
        } else {
          props = transformEobProcessing(event, filteredEvents, index)
        }
        break
      case 'document':
        props = transformOnshoreChange(event)
        break
    }

    if (props) result.push(props)
  }

  return result
}

/** Returns event-type-specific searchable text (excludes user name — handled in shell) */
export function getSearchableText(event: AuditTrailEventProps): string {
  switch (event.type) {
    case 'cluster':
      return getClusterSearchableText(event)
    case 'classification':
      return getClassificationSearchableText(event)
    case 'data-extraction':
      return getDataExtractionSearchableText(event)
    case 'tagging':
      return getTaggingSearchableText(event)
    case 'patient-match':
      return getPatientMatchSearchableText(event)
    case 'payor':
      return getPayorSearchableText(event)
    case 'payor-change':
      return getPayorChangeSearchableText(event)
    case 'eob-payment-deposit':
      return getEobPaymentDepositSearchableText(event)
    case 'eob-duplication':
      return getEobDuplicationSearchableText(event)
    case 'eob-processing':
      return getEobProcessingSearchableText(event)
    case 'eob-processing-finished':
      return getEobProcessingFinishedSearchableText(event)
    case 'onshore-change':
      return getOnshoreChangeSearchableText(event)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEventComponent = ComponentType<{ event: any; searchQuery: string }>

/** Maps a typed event to its rendering component */
export function getEventComponent(event: AuditTrailEventProps): AnyEventComponent {
  switch (event.type) {
    case 'cluster':
      return ClusterEvent
    case 'classification':
      return ClassificationEvent
    case 'data-extraction':
      return DataExtractionEvent
    case 'tagging':
      return TaggingEvent
    case 'patient-match':
      return PatientMatchEvent
    case 'payor':
      return PayorEvent
    case 'payor-change':
      return PayorChangeEvent
    case 'eob-payment-deposit':
      return EobPaymentDepositEvent
    case 'eob-duplication':
      return EobDuplicationEvent
    case 'eob-processing':
      return EobProcessingEvent
    case 'eob-processing-finished':
      return EobProcessingFinishedEvent
    case 'onshore-change':
      return OnshoreChangeEvent
  }
}
